import { JsonRenderer } from "./renderers/JsonRenderer.js";
import { GeoJsonRenderer } from "./renderers/GeoJsonRenderer.js";
import { PanelRenderer } from "./renderers/PanelRenderer.js";
import { DebugLog } from "./DebugLog.js";

// DwMap Constants for better readability
export const PanelSide = {
  left: "left",
  right: "right",
  Left: "left",
  Right: "right",
};

export const AutoClose = {
  on: true,
  off: false,
  On: true,
  Off: false,
};

export class DwMap {
  constructor(selector, debug = false) {
    this.selector = selector;
    this.map = null;
    this.renderer = null;
    this.panelRenderer = null;
    this.debug = new DebugLog(debug);
    this.data = [];
    this.filterSelector = null;
    this.activeFilters = [];
    this.store = {}; // Internal data store for preloaded data
    this.markersUpdatedHandlers = [];
    this.options = {
      center: [52.1503, 4.4851],
      zoom: 13,
      maxZoom: 19,
      filterFn: () => true,
      transformerFn: (d) => d,
      panelSide: "right", // 'left' or 'right'
      panelWidth: "300px",
      tileUrl:
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
      copyright: "© OpenStreetMap © CARTO",
      loadErrorMessage: "Informatie kan niet worden geladen",
    };
  }

  withView(center, zoom) {
    this.options.center = center;
    this.options.zoom = zoom;
    return this;
  }

  withMaxZoom(maxZoom) {
    this.options.maxZoom = maxZoom;
    return this;
  }

  withOpenLeaf(tileUrl, copyright) {
    this.options.tileUrl = tileUrl;
    this.options.copyright = copyright;
    return this;
  }

  withMarker(iconUrlOrResolver, shadowUrl = null) {
    if (typeof iconUrlOrResolver === "function") {
      this.options.markerIconResolver = iconUrlOrResolver;
      this.options.markerIconUrl = null;
      this.options.markerShadowUrl = null;
    } else {
      this.options.markerIconUrl = iconUrlOrResolver;
      this.options.markerShadowUrl = shadowUrl;
      this.options.markerIconResolver = null;
    }
    // Update existing renderer if it exists
    if (this.renderer) {
      this.renderer.options.markerIconResolver = this.options.markerIconResolver;
      this.renderer.options.markerIconUrl = this.options.markerIconUrl;
      this.renderer.options.markerShadowUrl = this.options.markerShadowUrl;
    }
    return this;
  }

  withJson(url) {
    this.renderer = new JsonRenderer(url, {
      ...this.options,
      debug: this.debug,
    });
    this.setupDataHandling(url);
    return this;
  }

  withGeoJson(url) {
    this.renderer = new GeoJsonRenderer(url, {
      ...this.options,
      debug: this.debug,
    });
    return this;
  }

  withFilter(fn) {
    // Wrap the external filter function to pass our activeFilters and store
    this.options.filterFn = (item, debugLog) => {
      return fn(item, this.activeFilters, this.store, debugLog);
    };
    return this;
  }

  withFilters(selector) {
    this.filterSelector = selector;
    return this;
  }

  withPreloader(preloaderFn) {
    this.options.preloaderFn = preloaderFn;
    return this;
  }

  withTransformer(fn) {
    this.options.transformerFn = fn;
    return this;
  }

  withMoreInfo(html = "Meer informatie") {
    this.options.moreInfoHtml = html;
    return this;
  }

  withPopupContent(provider) {
    this.options.popupContentProvider = provider;
    // Update existing renderer if it exists
    if (this.renderer) {
      this.renderer.options.popupContentProvider = provider;
      if (this.renderer.popupRenderer) {
        this.renderer.popupRenderer.options.popupContentProvider = provider;
      }
    }
    return this;
  }

  withPanelContent(provider) {
    this.options.panelContentProvider = provider;
    // Update existing renderer if it exists
    if (this.renderer) {
      this.renderer.options.panelContentProvider = provider;
    }
    return this;
  }

  withLoadErrorMessage(message = "Informatie kan niet worden geladen") {
    this.options.loadErrorMessage = message;
    // Update existing renderer if it exists
    if (this.renderer) {
      this.renderer.options.loadErrorMessage = message;
      if (this.renderer.popupRenderer) {
        this.renderer.popupRenderer.options.loadErrorMessage = message;
      }
    }
    return this;
  }

  withDirectPanel(
    side = PanelSide.right,
    width = "300px",
    autoClose = AutoClose.on
  ) {
    this.options.panelSide = side;
    this.options.panelWidth = width;
    this.options.directPanel = true;
    this.options.autoClose = autoClose;
    // Update existing renderer if it exists
    if (this.renderer) {
      this.renderer.options.directPanel = true;
      if (this.renderer.popupRenderer) {
        this.renderer.popupRenderer.options.directPanel = true;
      }
    }
    return this;
  }

  withPanel(side = PanelSide.right, width = "300px", autoClose = AutoClose.on) {
    this.options.panelSide = side;
    this.options.panelWidth = width;
    this.options.directPanel = false;
    this.options.autoClose = autoClose;
    // Update existing renderer if it exists
    if (this.renderer) {
      this.renderer.options.directPanel = false;
      if (this.renderer.popupRenderer) {
        this.renderer.popupRenderer.options.directPanel = false;
      }
    }
    return this;
  }

  async draw() {
    if (!this.renderer) {
      throw new Error(
        "DwMap: No data source configured. Use withJson() or withGeoJson()."
      );
    }

    const container = document.querySelector(this.selector);
    if (!container) {
      throw new Error(
        `DwMap: Container element '${this.selector}' not found in DOM.`
      );
    }

    // Run preloader if provided
    if (this.options.preloaderFn) {
      this.debug.log("Running preloader...");
      try {
        await this.options.preloaderFn(this.store, this.debug);
        this.debug.log("Preloader completed, store:", this.store);
      } catch (error) {
        this.debug.warn("Preloader failed:", error);
        console.error("Preloader error:", error);
      }
    }

    this.map = L.map(container).setView(this.options.center, this.options.zoom);

    var tileLayer = L.tileLayer(this.options.tileUrl, {
      attribution: this.options.copyright,
      subdomains: "abcd",
      maxZoom: this.options.maxZoom,
    }).addTo(this.map);

    this.debug.attachTo(this.map, tileLayer);

    this.createPanel();

    // Pass panelRenderer reference to renderer if it exists
    if (this.renderer && this.panelRenderer) {
      this.renderer.panelRenderer = this.panelRenderer;
    }

    await this.redraw();

    // Setup map click handling for auto-close after map is created
    if (this.panelRenderer) {
      this.panelRenderer.setupMapClickHandling(this.map);
    }

    // Setup filter handling if filter selector is provided
    if (this.filterSelector) {
      this.setupFilterHandling();
    }

    return this;
  }

  redraw() {
    if (this.renderer && this.map) {
      return this.renderer.draw(this.map).then(() => {
        this.notifyMarkersUpdated();
      });
    }
    return Promise.resolve();
  }

    /**
   * Point the JSON renderer at a new URL, refresh cached data for the panel, and redraw markers.
   * Call after draw(). Only for use with withJson().
   *
   * @param {string} url Full URL (including query string) for the markers JSON endpoint.
   */
    reloadMarkers(url) {
      if (typeof url !== "string" || !url.trim()) {
        throw new Error("DwMap.reloadMarkers: url must be a non-empty string");
      }
      if (!this.renderer) {
        throw new Error(
          "DwMap.reloadMarkers: configure withJson() before reloadMarkers()"
        );
      }
      if (!this.map) {
        throw new Error("DwMap.reloadMarkers: call draw() before reloadMarkers()");
      }
      this.renderer.url = url;
      this.setupDataHandling(url);
      this.redraw();
    }

  onMarkersUpdated(handler) {
    if (typeof handler !== "function") {
      throw new Error("DwMap.onMarkersUpdated: handler must be a function");
    }
    this.markersUpdatedHandlers.push(handler);
    return this;
  }

  getMarkerBounds() {
    if (!this.renderer || typeof this.renderer.getMarkerBounds !== "function") {
      return null;
    }
    return this.renderer.getMarkerBounds();
  }

  fitToMarkers(options = {}) {
    if (!this.map) {
      throw new Error("DwMap.fitToMarkers: call draw() before fitToMarkers()");
    }

    const fitOptions = {
      padding: [24, 24],
      maxZoom: 15,
      singleMarkerZoom: 14,
      animate: true,
      ...options,
    };

    const bounds = this.getMarkerBounds();
    const count = this.getMarkerCount();
    if (!bounds || count === 0) {
      return false;
    }

    if (count === 1) {
      const targetZoom = Math.min(fitOptions.singleMarkerZoom, fitOptions.maxZoom);
      this.map.setView(bounds.getCenter(), targetZoom, { animate: fitOptions.animate });
      return true;
    }

    this.map.fitBounds(bounds, {
      padding: fitOptions.padding,
      maxZoom: fitOptions.maxZoom,
      animate: fitOptions.animate,
    });
    return true;
  }

  getMarkerCount() {
    if (!this.renderer || typeof this.renderer.getMarkerCount !== "function") {
      return 0;
    }
    return this.renderer.getMarkerCount();
  }

  notifyMarkersUpdated() {
    const detail = {
      count: this.getMarkerCount(),
      bounds: this.getMarkerBounds(),
    };

    for (const handler of this.markersUpdatedHandlers) {
      try {
        handler(detail);
      } catch (error) {
        this.debug.warn("DwMap onMarkersUpdated handler error:", error);
      }
    }

    const container = document.querySelector(this.selector);
    if (container) {
      container.dispatchEvent(
        new CustomEvent("dwmap:markers-updated", {
          detail,
        })
      );
    }
  }

  setupDataHandling(url) {
    // Store data for panel display
    this.debug.log("Loading data from:", url);
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        this.debug.log("Data loaded successfully:", data.length, "items");
        this.data = data;
        if (this.panelRenderer) {
          this.panelRenderer.setData(data);
        }
      })
      .catch((err) => {
        this.debug.warn("Error loading data:", err);
        console.error("Error loading data:", err);
      });
  }

  createPanel() {
    const container = document.querySelector(this.selector);
    if (!container) return;

    this.debug.log(
      "Creating panel:",
      this.options.panelSide,
      this.options.panelWidth,
      "autoClose:",
      this.options.autoClose
    );

    this.panelRenderer = new PanelRenderer(container, {
      panelSide: this.options.panelSide,
      panelWidth: this.options.panelWidth,
      autoClose: this.options.autoClose,
      debug: this.debug,
      panelContentProvider: this.options.panelContentProvider,
      loadErrorMessage: this.options.loadErrorMessage,
    });

    this.panelRenderer.create();
    this.panelRenderer.setupClickHandling();
    this.panelRenderer.setupWheelHandling();
    this.panelRenderer.setData(this.data);

    this.debug.log("Panel created successfully");
  }

  setupFilterHandling() {
    this.debug.log("Setting up filter handling for selector:", this.filterSelector);

    const checkboxes = document.querySelectorAll(
      `${this.filterSelector} input[type="checkbox"]`
    );
    this.debug.log("Found checkboxes:", checkboxes.length, checkboxes);

    // Attach filter change handling
    checkboxes.forEach((cb, index) => {
      this.debug.log(
        `Setting up listener for checkbox ${index}:`,
        cb.name,
        cb.checked
      );

      cb.addEventListener("change", (event) => {
        this.debug.log("Checkbox changed:", event.target.name, event.target.checked);

        // Collect all currently checked filter names
        this.activeFilters = Array.from(
          document.querySelectorAll(
            `${this.filterSelector} input[type="checkbox"]:checked`
          )
        ).map((cb) => cb.name);

        this.debug.log("Active filters:", this.activeFilters);

        // Re-draw the map with new filter
        this.redraw();
      });
    });
  }
}
